/**
 * Registry for React component renderers.
 *
 * This allows incremental migration from jQuery plugins to React components.
 * When a component type has a registered React renderer, ComponentBase will
 * render it directly instead of using the jQuery plugin.
 */

import type { ComponentType as ReactComponentType } from 'react';
import {
  type AnyMeasureBlockComponent,
  ComponentType,
  type BlockComponent,
  type GlobalFilters,
  type GlobalParameterOverrides,
} from 'types';

// Import React component renderers
import { ComponentAssetSelector } from '../react/component-asset-selector/component-asset-selector';
import { ComponentAuthor } from '../react/component-author';
import { ComponentEmbeddedHtml } from '../react/component-embedded-html';
import { ComponentGlobalFilter } from '../react/component-global-filter/component-global-filter';
import { ComponentHero } from '../react/component-hero';
import { ComponentHorizontalRule } from '../react/component-horizontal-rule';
import { ComponentHtml } from '../react/component-html';
import { ComponentImage } from '../react/component-image';
import { ComponentInSituVisualization } from '../react/component-in-situ-visualization';
import { ComponentMap } from '../react/component-map';
import { ComponentSpacer } from '../react/component-spacer';
import { ComponentStoryTile } from '../react/component-story-tile';
import { ComponentYoutubeVideo } from '../react/component-youtube-video';
import { ComponentMeasure } from '../react/component-measure/component-measure';
import { ComponentVizCanvas } from '../react/component-viz-canvas/component-viz-canvas';

/**
 * Props passed to all React component renderers.
 * This mirrors the props passed to jQuery plugins via pluginProps.
 */
export interface ReactRendererProps {
  componentData: BlockComponent | AnyMeasureBlockComponent;
  theme: string;
  editMode: boolean;
  blockId: string;
  componentIndex: number;
  additionalFilters: GlobalFilters;
  parameterOverrides: GlobalParameterOverrides;
}

type ReactRenderer = ReactComponentType<ReactRendererProps>;

/**
 * Static map of component types to their React renderers.
 * Add new React component renderers here as they are migrated from jQuery.
 */
const reactRenderers: Partial<Record<ComponentType, ReactRenderer>> = {
  [ComponentType.ASSET_SELECTOR]: ComponentAssetSelector,
  [ComponentType.CLASSIC_VIZ]: ComponentAssetSelector,
  [ComponentType.AUTHOR]: ComponentAuthor,
  [ComponentType.EMBEDDED_HTML]: ComponentEmbeddedHtml,
  [ComponentType.GLOBAL_FILTER]: ComponentGlobalFilter,
  [ComponentType.HERO]: ComponentHero,
  [ComponentType.HORIZONTAL_RULE]: ComponentHorizontalRule,
  [ComponentType.HTML]: ComponentHtml,
  [ComponentType.HTML_TABLE_OF_CONTENTS]: ComponentHtml,
  [ComponentType.IMAGE]: ComponentImage,
  [ComponentType.MAP]: ComponentMap,
  [ComponentType.FEATURE_MAP]: ComponentMap,
  [ComponentType.REGION_MAP]: ComponentMap,
  [ComponentType.SPACER]: ComponentSpacer,
  [ComponentType.STORY_TILE]: ComponentStoryTile,
  [ComponentType.STORY_WIDGET]: ComponentStoryTile,
  [ComponentType.YOUTUBE]: ComponentYoutubeVideo,
  [ComponentType.MEASURE_CARD]: ComponentMeasure,
  [ComponentType.MEASURE_CHART]: ComponentMeasure,
  [ComponentType.BAR_CHART]: ComponentInSituVisualization,
  [ComponentType.CALENDAR]: ComponentInSituVisualization,
  [ComponentType.COLUMN_CHART]: ComponentInSituVisualization,
  [ComponentType.COMBO_CHART]: ComponentInSituVisualization,
  [ComponentType.HISTOGRAM]: ComponentInSituVisualization,
  [ComponentType.PIE_CHART]: ComponentInSituVisualization,
  [ComponentType.SCATTER_CHART]: ComponentInSituVisualization,
  [ComponentType.TIMELINE_CHART]: ComponentInSituVisualization,
  [ComponentType.VIZ_CANVAS]: ComponentVizCanvas,
};

/**
 * Get the React renderer for a component type, if one exists.
 */
export const getReactRenderer = (type: ComponentType): ReactRenderer | undefined => {
  return reactRenderers[type];
};
